import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { hikingPath } from '../utils/paths';
import {
  TRAIL_DIFFICULTIES,
  TRAIL_TERRAINS,
  TRAIL_ROUTE_SHAPES,
  formatDistance,
  formatDuration,
  formatElevation,
  formatTrailDifficulty,
  formatTrailRouteShape,
  formatTrailTerrain,
} from '../config/hikerProfileOptions';
import PhotoGallery from './PhotoGallery';
import './HikingTrailsPage.css';

const EMPTY_FORM = {
  title: '',
  description: '',
  parkName: '',
  distanceKm: '',
  elevationGainM: '',
  estimatedDurationMinutes: '',
  terrain: 'DIRT',
  difficulty: 'MODERATE',
  routeShape: 'LOOP',
  mapUrl: '',
  trailheadLocation: '',
  endLocation: '',
  dogFriendly: false,
  permitRequired: false,
  parkingNotes: '',
  published: false,
};

const HikingTrailsPage = () => {
  const { id: clubId } = useParams();
  const { user, isAdmin } = useAuth();
  const [club, setClub] = useState(null);
  const [trails, setTrails] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [canManage, setCanManage] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const clubData = await api.getClubById(clubId);
      setClub(clubData);
      const isOwner = user?.id && clubData.adminUserId === user.id;
      const manage = !!(isOwner || isAdmin());
      setCanManage(manage);
      const data = await api.getHikingTrails(clubId, !manage);
      setTrails(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load trails');
    } finally {
      setLoading(false);
    }
  }, [clubId, user?.id, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const startEdit = (trail) => {
    setEditingId(trail.id);
    setForm({
      title: trail.title || '',
      description: trail.description || '',
      parkName: trail.parkName || '',
      distanceKm: trail.distanceKm != null ? String(trail.distanceKm) : '',
      elevationGainM: trail.elevationGainM != null ? String(trail.elevationGainM) : '',
      estimatedDurationMinutes:
        trail.estimatedDurationMinutes != null ? String(trail.estimatedDurationMinutes) : '',
      terrain: trail.terrain || 'DIRT',
      difficulty: trail.difficulty || 'MODERATE',
      routeShape: trail.routeShape || 'LOOP',
      mapUrl: trail.mapUrl || '',
      trailheadLocation: trail.trailheadLocation || '',
      endLocation: trail.endLocation || '',
      dogFriendly: !!trail.dogFriendly,
      permitRequired: !!trail.permitRequired,
      parkingNotes: trail.parkingNotes || '',
      published: !!trail.published,
    });
    setSuccess('');
    setError('');
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    description: form.description.trim() || null,
    parkName: form.parkName.trim() || null,
    distanceKm: form.distanceKm ? Number(form.distanceKm) : null,
    elevationGainM: form.elevationGainM ? Number(form.elevationGainM) : null,
    estimatedDurationMinutes: form.estimatedDurationMinutes
      ? Number(form.estimatedDurationMinutes)
      : null,
    terrain: form.terrain || null,
    difficulty: form.difficulty || null,
    routeShape: form.routeShape || null,
    mapUrl: form.mapUrl.trim() || null,
    trailheadLocation: form.trailheadLocation.trim() || null,
    endLocation: form.endLocation.trim() || null,
    dogFriendly: form.dogFriendly,
    permitRequired: form.permitRequired,
    parkingNotes: form.parkingNotes.trim() || null,
    published: !!form.published,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const payload = buildPayload();
      if (editingId) {
        await api.updateHikingTrail(editingId, payload, user.id);
        setSuccess('Trail updated');
      } else {
        await api.createHikingTrail(clubId, payload, user.id);
        setSuccess('Trail created');
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save trail');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (trail) => {
    try {
      await api.publishHikingTrail(trail.id, user.id, !trail.published);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update publish state');
    }
  };

  const handleDelete = async (trailId) => {
    if (!window.confirm('Delete this trail?')) return;
    try {
      await api.deleteHikingTrail(trailId, user.id);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to delete trail');
    }
  };

  const handlePhotos = async (trailId, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      await api.uploadHikingTrailPhotos(trailId, files, user.id);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to upload photos');
    }
  };

  if (loading) return <div className="loading">Loading trails...</div>;

  return (
    <div className="hiking-trails-page">
      <div className="hiking-trails-page__head">
        <div>
          <p className="hiking-trails-page__eyebrow">
            <Link to={hikingPath(`/clubs/${clubId}`)}>{club?.name || 'Club'}</Link>
          </p>
          <h1>Trails</h1>
          <p>Organizers publish trails. Hikers pick one when an outing is scheduled.</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {canManage && (
        <form className="hiking-trails-page__form" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Edit trail' : 'Publish a trail'}</h2>
          <label>
            Title
            <input name="title" value={form.title} onChange={onChange} required maxLength={100} />
          </label>
          <label>
            Park or preserve
            <input
              name="parkName"
              value={form.parkName}
              onChange={onChange}
              placeholder="Alum Creek State Park"
            />
          </label>
          <label>
            Description
            <textarea name="description" value={form.description} onChange={onChange} rows={3} />
          </label>
          <div className="hiking-trails-page__row">
            <label>
              Distance (km)
              <input
                name="distanceKm"
                type="number"
                min="0.1"
                step="0.1"
                value={form.distanceKm}
                onChange={onChange}
              />
            </label>
            <label>
              Elevation gain (m)
              <input
                name="elevationGainM"
                type="number"
                min="0"
                step="1"
                value={form.elevationGainM}
                onChange={onChange}
              />
            </label>
            <label>
              Time on trail (min)
              <input
                name="estimatedDurationMinutes"
                type="number"
                min="15"
                max="1440"
                step="15"
                value={form.estimatedDurationMinutes}
                onChange={onChange}
              />
            </label>
          </div>
          <div className="hiking-trails-page__row">
            <label>
              Terrain
              <select name="terrain" value={form.terrain} onChange={onChange}>
                {TRAIL_TERRAINS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Difficulty
              <select name="difficulty" value={form.difficulty} onChange={onChange}>
                {TRAIL_DIFFICULTIES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Route shape
              <select name="routeShape" value={form.routeShape} onChange={onChange}>
                {TRAIL_ROUTE_SHAPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Map / AllTrails / GPX URL
            <input name="mapUrl" value={form.mapUrl} onChange={onChange} placeholder="https://" />
          </label>
          <div className="hiking-trails-page__row">
            <label>
              Trailhead location
              <input
                name="trailheadLocation"
                value={form.trailheadLocation}
                onChange={onChange}
                placeholder="3993 Hollenback Rd, Lewis Center, OH"
              />
            </label>
            <label>
              End location
              <input name="endLocation" value={form.endLocation} onChange={onChange} />
            </label>
          </div>
          <label>
            Parking notes
            <textarea
              name="parkingNotes"
              value={form.parkingNotes}
              onChange={onChange}
              rows={2}
              placeholder="Gravel lot fills by 9am; overflow across the road."
            />
          </label>
          <div className="hiking-trails-page__checks">
            <label className="hiking-trails-page__check">
              <input
                type="checkbox"
                name="dogFriendly"
                checked={form.dogFriendly}
                onChange={onChange}
              />
              Dog friendly
            </label>
            <label className="hiking-trails-page__check">
              <input
                type="checkbox"
                name="permitRequired"
                checked={form.permitRequired}
                onChange={onChange}
              />
              Permit required
            </label>
            <label className="hiking-trails-page__check">
              <input
                type="checkbox"
                name="published"
                checked={form.published}
                onChange={onChange}
              />
              Published for hikers
            </label>
          </div>
          <div className="hiking-trails-page__form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Save changes' : 'Create trail'}
            </button>
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>
        </form>
      )}

      <div className="hiking-trails-page__list">
        {trails.length === 0 && <p>No trails yet.</p>}
        {trails.map((trail) => (
          <article key={trail.id} className="hiking-trails-page__card">
            <div className="hiking-trails-page__card-top">
              <div>
                <h3>
                  {trail.title}
                  {!trail.published && <span className="hiking-trails-page__badge">Draft</span>}
                </h3>
                {trail.parkName && <p>{trail.parkName}</p>}
                {trail.distanceKm != null && (
                  <p className="hiking-trails-page__stat">{formatDistance(trail.distanceKm)}</p>
                )}
                <p>
                  {[
                    trail.difficulty && formatTrailDifficulty(trail.difficulty),
                    trail.terrain && formatTrailTerrain(trail.terrain),
                    trail.routeShape && formatTrailRouteShape(trail.routeShape),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {trail.elevationGainM != null && <p>{formatElevation(trail.elevationGainM)}</p>}
                {trail.estimatedDurationMinutes != null && (
                  <p>About {formatDuration(trail.estimatedDurationMinutes)} on trail</p>
                )}
                {trail.description && <p>{trail.description}</p>}
                {trail.trailheadLocation && <p>Trailhead: {trail.trailheadLocation}</p>}
                {trail.endLocation && <p>Ends: {trail.endLocation}</p>}
                {trail.parkingNotes && <p>Parking: {trail.parkingNotes}</p>}
                <p className="hiking-trails-page__flags">
                  {trail.dogFriendly ? '🐕 Dog friendly' : '🚫 No dogs'}
                  {trail.permitRequired ? ' · Permit required' : ''}
                </p>
                {trail.mapUrl && (
                  <a href={trail.mapUrl} target="_blank" rel="noreferrer">
                    Open map
                  </a>
                )}
              </div>
              {canManage && (
                <div className="hiking-trails-page__card-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => startEdit(trail)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => togglePublish(trail)}
                  >
                    {trail.published ? 'Unpublish' : 'Publish'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleDelete(trail.id)}
                  >
                    Delete
                  </button>
                  <label className="btn btn-secondary hiking-trails-page__file">
                    Add photos
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      onChange={(e) => handlePhotos(trail.id, e.target.files)}
                    />
                  </label>
                </div>
              )}
            </div>
            <PhotoGallery photos={trail.photoUrls || []} />
          </article>
        ))}
      </div>
    </div>
  );
};

export default HikingTrailsPage;
